/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import { Injectable } from '@angular/core';
import { ConfigService } from './config.service';
import { HttpClient, HttpErrorResponse, HttpParams, HttpStatusCode } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PropulsionTypeService {

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  /**
   * Sends a get request to retrieve list of all propulsion types in the database.
   */
  getPropulsionTypes(): Observable<any[]> {
    return this.http.get<any>(`${this.configService.baseApiUrl}manage-propulsion-types`)
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a post request to add a new propulsion type to the database.
   * @param propulsionType user inputted propulsion type to add
   */
  addPropulsionType(propulsionType): Observable<any> {
    const params = new HttpParams().set('propulsionType', propulsionType);
    return this.http.post(`${this.configService.baseApiUrl}manage-propulsion-types`, null, { params })
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a put request to update an existing propulsion type in the database.
   * @param propulsionId  ID value of user's selected propulsion type to update.
   * @param newPropulsionType New value to replace existing value.
   */
  updatePropulsionType(propulsionId, newPropulsionType): Observable<any> {
    const params = new HttpParams().set('propulsionId', propulsionId).set('newPropulsionType', newPropulsionType);
    return this.http.put(`${this.configService.baseApiUrl}manage-propulsion-types`, null, { params })
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a delete request to remove given propulsion type from the database.
   * @param propulsionTypeId  ID value of user's selected propulsion type to delete.
   */
  deletePropulsionType(propulsionTypeId): Observable<any> {
    return this.http.delete(`${this.configService.baseApiUrl}manage-propulsion-types?propulsionId=` + encodeURI(propulsionTypeId))
      .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   * @return void
   */
  handleError(errorResponse: HttpErrorResponse): any {
    let errorMessage = 'An unexpected error occurred.';
    switch (errorResponse.status) {
      case HttpStatusCode.BadRequest: {
        if (errorResponse.error && errorResponse.error.message) {
          errorMessage = errorResponse.error.message;
        } else {
          errorMessage = 'Error loading Propulsion Types.';
        }
        break;
      }
    }
    return throwError(errorMessage);
  }
}
